import { defineComponent as _defineComponent } from 'vue'
import {
  inject,
  onActivated,
  onDeactivated,
  onMounted,
  onBeforeUnmount,
  provide,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import Opinion from "@/views/main/Opinion.vue";
import { useUserStore } from "@/stores/userStore";
import { useLineStore } from "@/stores/lineStore";
import { useBbsStore } from "@/stores/bbsStore";
import { useSurveyStore } from "@/stores/surveyStore";
import { IBusStop } from "@/domain/entities/busStop";
import { INoticeInfoData } from "@/domain/entities/bbs";
import { IBusLocation } from "@/domain/entities/lineInfo";
import dayjs from "dayjs";
import deviceHelper from "@/utils/deviceInfo";
import {
  BUS_LOCATION_REFRESH_TIME_MILLIS,
  BusStopEventCode,
  PROVIDE_KEY_BUS_LOCATIONS,
  PROVIDE_KEY_ARRIVAL_TIME_REMAINING,
  STORAGE_KEY_COMPLAINTS_SURVEY,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowToastMessage,
  LineTypeCode,
  ON_BOARD_LINE_CALLING_TIME_MILLIS,
  ShowErrorModal,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_UPDATE_GET_OFF_BUS_STOP_ID,
  SERVICE_EVALUATION_POPUP,
  PROVIDE_KEY_HANDLE_CHECK_PASSWORD_EXPIRED,
  HandleCheckPasswordExpired,
  ERROR_CODE_BOOK_BLOCKED,
  PROVIDE_KEY_IS_BUS_RUNNING,
  PROVIDE_KEY_TURN_BUSES_REMAIN_SEAT_COUNT,
  RouterName,
  FavoriteLinesViewType,
  BUS_LOCATION_CACHED_TIME_MILLIS,
} from "@/types";
import Page from "@/components/shared/Page.vue";
import MapOfBusStop from "@/views/map/MapOfBusStop.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import MainNavigationBar from "@/components/main/MainNavigationBar.vue";
import useConvertTimeStrToMinutes from "@/use/useConvertTimeStrToMinutes";
import useConvertCurrentTimeToMinutes from "@/use/useConvertCurrentTimeToMinutes";
import shouldShowComplaintsSurvey from "@/use/useCheckComplaintsSurveyTarget";
import { computed } from "@vue/reactivity";
import { useServiceStore } from "@/stores/serviceStore";
import SurveyNoticeModal from "@/components/shared/modal/base/SurveyNoticeModal.vue";
import ServiceEvaluation from "@/components/mypage/ServiceEvaluation.vue";
import ReportModal from "@/components/shared/modal/ReportModal.vue";
import { IOnBoardLine } from "@/domain/entities/onBoardLine/onBoardLine.types";
import TimeTableModal from "@/components/line/TimeTableModal.vue";
import BusInfoModal from "@/components/shared/modal/BusInfoModal.vue";
import BusStopLayoutModal from "@/components/line/BusStopLayoutModal.vue";
import { useGetUserInfo } from "@/composables/user/useGetUserInfo";
import { useHandleUseBlock } from "@/composables/block/useHandleUseBlock";
import { useTurnBusLastSeatCountList } from "@/composables/line/useTurnBusLastSeatCountList";
import UserBlockingMessage from "@/components/main/UserBlockingMessage.vue";
import WelcomeMessages from "@/components/main/WelcomeMessages.vue";
import FavoriteListView from "@/components/main/FavoriteListView.vue";
import UtilityButton from "@/components/main/UtilityButton.vue";

import FavoriteCardsView from "@/components/main/FavoriteCardsView.vue";
import BoardingLineInfoModal from "@/components/shared/modal/BoardingLineInfoModal.vue";
import { useBusLocationFetchConfig } from "@/composables/line/useBusLocationFetchConfig";
import { useBusLocationDrivingStateManager } from "@/composables/line/useBusLocationDrivingStateManager";
import { IFavoriteLine } from "@/domain/entities/favoriteLine";
import CampaignBannerSwiper from "@/components/main/CampaignBannerSwiper.vue";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import PageModal from "@/components/shared/modal/base/PageModal.vue";


const __default__ = {
  name: "MainLogin",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  isColsedNoticeModal: {
    type: Boolean,
  },
},
  emits: ["toggleShowMenu", "showNoticeModal"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();



const emit = __emit;

const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;
const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const router = useRouter();
const route = useRoute();

const serviceStore = useServiceStore();
const serviceInfo = computed(() => serviceStore.serviceInfo);
const shouldShowFavoriteLinesCardView = computed(
  () => serviceInfo.value?.favorieLinesViewType === FavoriteLinesViewType.CARD
);

const userStore = useUserStore();
const surveyStore = useSurveyStore();
const lineStore = useLineStore();
const bbsStore = useBbsStore();

const lineName = computed(() =>
  useGetLineRouteName(
    currentFavoriteLine.value?.lineTypeCode,
    currentFavoriteLine.value?.routeName,
    currentFavoriteLine.value?.lineNum
  )
);

// 이용 제한
const { isBookBlocked, isBoardBlocked, blockReleaseDtmHtmlStr } =
  useHandleUseBlock();

const userInfo = computed(() => userStore.userInfo);
const favoriteLines = computed<IFavoriteLine[]>(() => lineStore.favoriteLines);

const shouldShowAlertForPushDisabled = ref(false);
const shouldShowRequestReview = ref(false);
const shouldShowMyPage = ref(false);
shouldShowMyPage.value = userStore.isMenuOpened;

const cardUpdateKey = ref(0);
const allCardsUpdateKey = ref(0);
const transitionName = ref("slide-in-left");
const noticeInfos = ref<INoticeInfoData[]>([]);
const latestNoticeInfo = ref<INoticeInfoData>();

if (userStore.isMenuOpened) {
  transitionName.value = "";
  setTimeout(() => {
    transitionName.value = "slide-in-left";
  }, 300);
}

/** 서비스평가 공지 팝업 모달 처리 */
const shouldShowSurveyNotice = ref(false);
const showSurveyNoticeModal = () => {
  if (checkCookieExists(SERVICE_EVALUATION_POPUP)) return;
  shouldShowSurveyNotice.value = true;
};
const onCloseSurveyNotice = () => {
  shouldShowSurveyNotice.value = false;
};

// 즐겨찾기 리스트 뷰 - 승차중인 노선 정보 모달
const shouldShowBoardingLineInfoModal = ref(false);
const toggleShowBoardingLineInfoModal = (show: boolean) => {
  shouldShowBoardingLineInfoModal.value = show;
};

const handleClickBoardingStatusButton = async (line: IFavoriteLine) => {
  updateCurrentFavoriteLine(line);
  await setIntervalBusLocation(true);
  toggleShowBoardingLineInfoModal(true);
};

// 서비스 평가 모달 처리
const shouldShowServiceEvaluation = ref(false);
const toggleShowServiceEvaluation = (show: boolean) => {
  shouldShowServiceEvaluation.value = show;
};

// 공지 팝업 모달 처리
const showNoticeModal = () => {
  const popupNoticeInfos = noticeInfos.value.filter(
    (noticeinfo) => noticeinfo.isPopup && !checkCookieExists(noticeinfo.id)
  );
  emit("showNoticeModal", popupNoticeInfos);
};
const checkCookieExists = (key: string) => {
  const cookieData = document.cookie.split(";");
  const index = cookieData.findIndex((cookie) => cookie.includes(key));
  return index !== -1;
};

// 예약 실패 모달 처리
const shouldShowAlertForBookFail = ref(false);
const showAlertForBookFail = () => {
  shouldShowAlertForBookFail.value = true;
};

// 신고하기 모달 처리
const shouldShowReportModal = ref(false);
const toggleShowReportModal = (show: boolean) => {
  shouldShowReportModal.value = show;
};

// 버스 사진 모달 처리
const shouldShowBusImage = ref(false);
const toggleShowBusImage = (show: boolean) => {
  shouldShowBusImage.value = show;
};

/** 각종 버튼들 눌렀을때의 처리 */

// 정류장 위치 아이콘 버튼 처리
const targetBusStop = ref<IBusStop>();
const showBusStopLocation = (busStop: IBusStop) => {
  targetBusStop.value = busStop;
  if (busStop.shouldShowBusStopLayout) {
    toggleShowBusStopLayoutModal(true);
  } else {
    const today = dayjs().format("YYYY-MM-DD HH:mm");
    router.push({
      name: RouterName.MapOfLineBusStops,
      params: { turnId: onBoardLine.value?.lineTurnUid, busStopId: busStop.id },
      query: {
        targetDateTime: encodeURIComponent(today),
      },
    });
  }
};

// 정류장 지도 화면 처리
const shouldShowMap = ref(false);
const toggleShowMap = (show: boolean) => {
  shouldShowMap.value = show;
};

// 정류장 배치도 화면 처리
const shouldShowBusStopLayoutModal = ref(false);
const toggleShowBusStopLayoutModal = (show: boolean) => {
  shouldShowBusStopLayoutModal.value = show;
};

// 리뷰 및 의견 남기기 처리
const showReviewModal = ref(false);
const showOpinionModal = ref(false);
const shouldShowOpinion = ref(false);

const onClickPleasant = (isPleasant: boolean) => {
  localStorage.setItem(
    STORAGE_KEY_COMPLAINTS_SURVEY,
    dayjs().format("YYYY-MM-DD HH:mm")
  );
  shouldShowRequestReview.value = false;
  showReviewModal.value = isPleasant;
  showOpinionModal.value = !isPleasant;
};
const toggleShowOpinionPage = (show: boolean) => {
  showOpinionModal.value = false;
  shouldShowOpinion.value = show;
};

const busLocationUpdateIntervalMillis = computed(
  () =>
    serviceStore.serviceInfo?.busLocationUpdateIntervalMillis ||
    BUS_LOCATION_REFRESH_TIME_MILLIS
);

// 버스 위치 갱신 버튼 눌렸을 때 처리
const lastRefreshTime = ref<dayjs.Dayjs>(dayjs());
const updateBusLocation = (forOnBoardLine = false) => {
  const now = dayjs();
  const diffMillis = now.diff(lastRefreshTime.value);
  if (diffMillis < BUS_LOCATION_CACHED_TIME_MILLIS) return;
  lastRefreshTime.value = now;
  getBusLocation(forOnBoardLine);
};

const currentFavoriteLine = ref<IFavoriteLine>();
const isInsideShuttle = computed(
  () => currentFavoriteLine.value?.lineTypeCode === LineTypeCode.Inside
);

const updateCurrentFavoriteLine = (favoriteLine: IFavoriteLine) => {
  currentFavoriteLine.value = favoriteLine;
};

const updateFavoriteLine = (favoriteLine: IFavoriteLine) => {
  const { turnId, getOnBusStop, getOffBusStop } = favoriteLine;

  const targetIndex = favoriteLines.value.findIndex((line) => {
    const isBookable = !!favoriteLine.bookIntervals?.length;
    return isBookable
      ? line.turnId === turnId &&
          line.getOnBusStop?.id === getOnBusStop?.id &&
          line.getOffBusStop?.id === getOffBusStop?.id
      : line.turnId === turnId;
  });

  if (targetIndex === -1) return;

  favoriteLines.value[targetIndex] = favoriteLine;
};

const onChangedCheck = (checked: boolean) => {
  if (!currentFavoriteLine.value) return;

  currentFavoriteLine.value.monthlyBookChecked = checked;
};

/** 버스 위치 가져오기 */

// Favorite Line의 turnId와 일치하는 버스 위치
const currentLocation = ref<IBusLocation>();

// turnId 별 버스위치 리스트
const busLocations = ref<IBusLocation[]>([]);
provide(PROVIDE_KEY_BUS_LOCATIONS, busLocations);

const { turnBusLastSeatCountList } = useTurnBusLastSeatCountList(busLocations);
provide(PROVIDE_KEY_TURN_BUSES_REMAIN_SEAT_COUNT, turnBusLastSeatCountList);

const {
  updateHasAllBusesArrivedLastStop,
  updateHasAllBusesEndedDriving,
  isBusRunning,
  updateBusRunningStatus,
  shouldGetBusLocation,
} = useBusLocationDrivingStateManager();
provide(PROVIDE_KEY_IS_BUS_RUNNING, isBusRunning);

const { isLoggedIn, lastBusStopArrivalThresholdMin } =
  useBusLocationFetchConfig();

const getFirstStop = () => {
  if (!currentFavoriteLine.value) return undefined;
  if (!isInsideShuttle.value) {
    return currentFavoriteLine.value.busStops[0];
  }

  const shuttleLineInfo = lineStore.getLineInfoByLineId(
    currentFavoriteLine.value.lineId
  );
  if (!shuttleLineInfo) return undefined;

  return shuttleLineInfo.turns[0].firstStop;
};

const getLastStop = () => {
  if (!currentFavoriteLine.value) return undefined;

  if (!isInsideShuttle.value) {
    const busStops = currentFavoriteLine.value.busStops;
    const lastBusStop = busStops[busStops.length - 1]; // 종점 정류장
    return lastBusStop;
  }

  const shuttleLineInfo = lineStore.getLineInfoByLineId(
    currentFavoriteLine.value.lineId
  );
  if (!shuttleLineInfo) return undefined;

  const lastBusStop =
    shuttleLineInfo.turns[shuttleLineInfo.turns.length - 1].lastStop;
  return lastBusStop;
};

const getBusLocation = async (forOnBoardLine = false) => {
  if (!currentFavoriteLine.value) return;

  const result = await shouldGetBusLocation(
    isLoggedIn.value,
    lastBusStopArrivalThresholdMin.value,
    currentFavoriteLine.value.lineTypeCode,
    forOnBoardLine ? onBoardLine.value?.campusName : undefined, // 탑승중일때만 실시간 버스 위치를 보여주고 있으므로
    getFirstStop(),
    getLastStop()
  );

  if (!result.shouldGetLocation) {
    remainingTimeStr.value = "정보없음";
    isBusRunning.value = false;
    removeIntervalBusLocation();
    return;
  }

  const busLocationResult = await lineStore.getBusLocations(
    currentFavoriteLine.value.lineId,
    isInsideShuttle.value ? undefined : currentFavoriteLine.value.turnId,
    forOnBoardLine ? onBoardLine.value?.allocUid : undefined
  );

  const locations = busLocationResult.data;
  const location = locations?.find(
    (location) => location.lineTurnUid === currentFavoriteLine.value?.turnId
  );
  if (!location) return;

  busLocations.value = locations ?? [];
  currentLocation.value = location;
  updateHasAllBusesArrivedLastStop(busLocations.value);
  updateHasAllBusesEndedDriving(busLocations.value);
  updateBusRunningStatus(busLocations.value, result.shouldGetLocation);
  updateRemainingTimeStr();
};

/** 30초마다 실시간 버스 위치 갱신  */
const intervalBusLocation = ref();
const setIntervalBusLocation = async (onBoardLine = false) => {
  if (route.name !== RouterName.MainLogin) return;

  if (!currentFavoriteLine.value) return;

  removeIntervalBusLocation();
  await getBusLocation(onBoardLine);

  intervalBusLocation.value = setInterval(
    () => getBusLocation(onBoardLine),
    busLocationUpdateIntervalMillis.value
  );
};
const removeIntervalBusLocation = () => {
  clearInterval(intervalBusLocation.value);
};

/** 버스 도착 시간 계산 */
const remainingTimeStr = ref("정보없음");
provide(PROVIDE_KEY_ARRIVAL_TIME_REMAINING, remainingTimeStr);

// 버스 이벤트가 있는 정류장 중 마지막 정류장의 인덱스
const getLastBusStopEventIndex = () => {
  let lastBusStopEventIndex = -1;
  currentLocation.value?.busStopEvents.forEach((event, index) => {
    if (event.busStopEventCode !== BusStopEventCode.None) {
      lastBusStopEventIndex = index;
    }
  });
  return lastBusStopEventIndex;
};

// 마지막 이벤트가 발생한 시각
const getLastEventTime = () => {
  const lastEventCollectedTs =
    currentLocation.value?.busStopEvents[getLastBusStopEventIndex()]
      .collectedTs;
  return dayjs(lastEventCollectedTs).format("HH:mm");
};

const updateRemainingTimeStr = () => {
  remainingTimeStr.value = "정보없음";
  if (!currentFavoriteLine.value) return;
  if (!currentFavoriteLine.value.getOnBusStop) return;

  const busStops = currentFavoriteLine.value.busStops;
  const lastBusStopEventIndex = getLastBusStopEventIndex(); // 버스 이벤트가 있는 정류장 중 마지막 정류장의 인덱스
  const getOnBusStop = currentFavoriteLine.value.getOnBusStop; // 탑승정류장
  const currentTimeMinutes = useConvertCurrentTimeToMinutes();

  if (getOnBusStop.order! < lastBusStopEventIndex) return; // 현재 정류장을 이미 지나간 경우

  // 버스 이벤트가 아직 기록되지 않은 경우(첫번째 정류장 진입 전)
  if (lastBusStopEventIndex < 0) {
    if (!currentLocation.value) return;
    const timeDiffMin =
      useConvertTimeStrToMinutes(getOnBusStop.arrivalTime) - currentTimeMinutes;
    if (timeDiffMin < -10) {
      remainingTimeStr.value = "정보없음";
      return;
    }
    remainingTimeStr.value =
      timeDiffMin <= 0 ? "곧 도착" : `약 ${timeDiffMin}분 후 도착`;
    return;
  }

  // 마지막으로 발생한 버스 이벤트가 현재 정류장인 경우
  if (getOnBusStop.order! === lastBusStopEventIndex) {
    const getOnBusStopEvent =
      currentLocation.value?.busStopEvents[lastBusStopEventIndex];
    remainingTimeStr.value =
      getOnBusStopEvent?.busStopEventCode === BusStopEventCode.Enter
        ? "곧 도착"
        : "정보없음";
    return;
  }

  // 아직 현재 정류장을 지나가지 않은 경우
  const lastEventBusStop = busStops[lastBusStopEventIndex]; // 마지막 이벤트가 발생한 정류장
  const lastEventTime = getLastEventTime(); // 마지막 이벤트가 발생한 시각
  const lastEventNextStop = busStops[lastBusStopEventIndex + 1]; // 마지막으로 발생한 버스 이벤트 정류장의 다음 정류장
  let remainTimeToNextStop =
    useConvertTimeStrToMinutes(lastEventNextStop.arrivalTime) -
    useConvertTimeStrToMinutes(lastEventBusStop.arrivalTime) -
    (currentTimeMinutes - useConvertTimeStrToMinutes(lastEventTime));
  remainTimeToNextStop = remainTimeToNextStop < 0 ? 0 : remainTimeToNextStop;

  // 마지막으로 발생한 버스 이벤트 정류장이 직전 정류장인 경우
  if (getOnBusStop.order! - lastBusStopEventIndex === 1) {
    const timeDiffMin = remainTimeToNextStop;
    remainingTimeStr.value =
      timeDiffMin <= 0 ? "곧 도착" : `약 ${timeDiffMin}분 후 도착`;
    return;
  }

  const timeBetweenLastEventNextStopAndGetOnStop =
    useConvertTimeStrToMinutes(getOnBusStop.arrivalTime) -
    useConvertTimeStrToMinutes(lastEventNextStop.arrivalTime);
  const timeDiffMin =
    remainTimeToNextStop + timeBetweenLastEventNextStopAndGetOnStop;
  remainingTimeStr.value = `약 ${timeDiffMin}분 후 도착`;
};

/** 푸쉬 알림 설정 로직 */
const isNotificationAllowed = computed(() => userStore.isNotificationAllowed);
const pushTargetStop = ref<IBusStop>();
const stopClassStr = ref("승차");

const getTitleOfPushOnAlert = () => {
  return "기기 알림이 꺼져있어요!";
};

const updatePushStatusOnCard = async (targetBusStop: IBusStop) => {
  if (!currentFavoriteLine.value) return;

  const isGetOnBusStop = currentFavoriteLine.value.busStops.find(
    (busStop: IBusStop) => busStop.id === targetBusStop.id
  )?.isGetOnStop;
  if (isGetOnBusStop) {
    currentFavoriteLine.value.getOnNotice =
      !currentFavoriteLine.value.getOnNotice;
  } else {
    currentFavoriteLine.value.getOffNotice =
      !currentFavoriteLine.value.getOffNotice;
  }
  await lineStore.updatePushStatusOfFavoriteLine(currentFavoriteLine.value);
  cardUpdateKey.value += 1;
};

const disablePushMessage = () => {
  shouldShowAlertForPushDisabled.value = false;
  updatePushStatusOnCard(pushTargetStop.value!);
};

const shouldShowAlertForPushIsNotAllowed = ref(false);
const showAlertForPushIsNotAllowedIfNeeded = () => {
  shouldShowAlertForPushIsNotAllowed.value =
    !isNotificationAllowed.value && hasAnyBusStopNotificationIsOn();
};

const hasAnyBusStopNotificationIsOn = () => {
  return favoriteLines.value.some((favoriteLine: IFavoriteLine) => {
    return favoriteLine.busStops.some((busStop: IBusStop) => {
      return busStop.shouldNotice === true;
    });
  });
};

const pushSettingGuide = computed(() => {
  const serviceName = serviceInfo.value?.name ?? "해피로드";
  return deviceHelper.isAndroid()
    ? `[설정 > 애플리케이션 > ${serviceName} > 알림 > 알림 허용]`
    : `[설정 > 알림 > ${serviceName} > 알림 허용]`;
});

/** 앱스토어 이동 */
const goToAppStore = () => {
  showReviewModal.value = false;
  if (deviceHelper.isAndroid()) {
    window.android.goToAppStoreForReview();
  } else if (deviceHelper.isiOS()) {
    window.webkit.messageHandlers.goToAppStoreForReview.postMessage("");
  }
};

const goToLineInfoDetail = () => {
  if (!currentFavoriteLine.value) return;
  router.push({
    name: RouterName.LineInfoDetail,
    params: {
      lineId: currentFavoriteLine.value.lineId,
    },
    query: {
      turnId: currentFavoriteLine.value.turnId,
      getOnBusStopId: currentFavoriteLine.value!.getOnBusStop?.id ?? "",
      getOffBusStopId: currentFavoriteLine.value!.getOffBusStop?.id ?? "",
    },
  });
};

/**  셔틀 노선 시간표 보기 */
const targetBusStopForTimeTable = ref<IBusStop>();
const shouldShowTimeTable = ref(false);

const toggleShowTimeTable = (show: boolean) => {
  shouldShowTimeTable.value = show;
};

const showTimeTable = (busStop: IBusStop) => {
  targetBusStopForTimeTable.value = busStop;
  toggleShowTimeTable(true);
};

/** 유저 정보 가져오기 */
const getUserInfo = async () => {
  if (userInfo.value) return;
  // 메인화면 진입 시 사용자 정보가 없는 경우에 대한 방어 코드
  return await useGetUserInfo();
};

/** 공지사항 목록 가져오기 */
const getNoticeInfos = async () => {
  await bbsStore.getNoticeInfos();
  noticeInfos.value = bbsStore.noticeInfos;
  latestNoticeInfo.value = bbsStore.noticeInfos[0];
  showNoticeModal();
};

/** 서비스평가 질문 목록 가져오기 */
// const getServiceEvaluationContents = async () => {
//   const lineSurveyResult = await surveyStore.getServiceEvaluationContents();
//   if (lineSurveyResult.data?.id) {
//     showSurveyNoticeModal();
//   }
// };

/** FCM 토큰 설정 */
const updateFcmToken = () => {
  const token = userStore.pushToken;
  if (token) {
    userStore.updatePushToken(token);
  }
  showAlertForPushIsNotAllowedIfNeeded();
};

watch(
  () => isNotificationAllowed.value,
  (isAllowed) => {
    if (!isAllowed) {
      showAlertForPushIsNotAllowedIfNeeded();
    }
  }
);

const getSurveyInfo = async () => {
  if (shouldShowComplaintsSurvey()) {
    const complaintsSurveyResult =
      await surveyStore.getComplaintsSurveyContents();
    const complaintsSurvey = complaintsSurveyResult.data;
    shouldShowRequestReview.value = !!complaintsSurvey && !!complaintsSurvey.id;
  }
};

const getLineInfos = async () => {
  if (serviceStore.serviceInfo?.isPublicLineApi) return;

  const [lineInfosResult, lineInfosLiteResult] = await Promise.all([
    lineStore.getAllLineInfos(serviceInfo.value?.lineSortOrder),
    lineStore.getAllLineInfosLite(serviceInfo.value?.lineSortOrder),
  ]);

  // 둘 중 하나라도 실패하면 에러 처리하고 진행 중단
  if (!lineInfosResult.isSuccess || !lineInfosLiteResult.isSuccess) {
    // 우선순위: 기본 lineInfos 에러 > lite 에러
    const primaryError = !lineInfosResult.isSuccess
      ? lineInfosResult
      : lineInfosLiteResult;

    showErrorModal(
      "노선목록 오류",
      primaryError.errorMessage,
      primaryError.resultCode,
      primaryError.errorTime
    );

    return;
  }
};

const getFavoriteLines = async () => {
  const favoriteLinesResult = await lineStore.getFavoriteLines();
  if (!favoriteLinesResult.isSuccess) {
    showErrorModal(
      "즐겨찾기 노선 오류",
      favoriteLinesResult.errorMessage,
      favoriteLinesResult.resultCode,
      favoriteLinesResult.errorTime
    );
    return;
  }

  if (shouldShowFavoriteLinesCardView.value) {
    setIntervalBusLocation();
  }

  setIntervalOnBoardLine(shouldShowFavoriteLinesCardView.value);
};

/** 탑승중인 노선 정보 가져오기 */
const onBoardLine = ref<IOnBoardLine | undefined>();
const fullSeatLineRouteCount = ref(0);

const getOnBoardLine = async (shouldGetBusLocation: boolean) => {
  const onBoardLineResult = await lineStore.getOnBoardLine();

  if (!onBoardLineResult.isSuccess) {
    showToastMessage("탑승중인 노선 데이터를 가져올 수 없습니다");
    return;
  }

  if (!onBoardLineResult.data) return;

  const { onBoardLine: onBoardLineData, fullSeatLineRouteCount: count } =
    onBoardLineResult.data;

  onBoardLine.value = onBoardLineData;
  fullSeatLineRouteCount.value = count;

  if (shouldGetBusLocation) {
    getBusLocation();
  }
};

watch(
  () => onBoardLine.value,
  (newOnBoardLine) => {
    if (newOnBoardLine) return;

    removeIntervalBusLocation();
  }
);

// 탑승중 노선 API 호출 주기(밀리초)
const onBoardRequestIntervalMillis = computed(
  () =>
    serviceInfo.value?.onBoardRequestIntervalMillis ??
    ON_BOARD_LINE_CALLING_TIME_MILLIS
);

// 30초마다 탑승중인 노선 정보 가져오기
const intervalOnBoardLine = ref();
const setIntervalOnBoardLine = (isCardView: boolean) => {
  if (route.name !== RouterName.MainLogin) return;

  removeIntervalOnBoardLine();
  getOnBoardLine(isCardView);
  intervalOnBoardLine.value = setInterval(
    () => getOnBoardLine(isCardView),
    onBoardRequestIntervalMillis.value
  );
};
const removeIntervalOnBoardLine = () => {
  clearInterval(intervalOnBoardLine.value);
};

// 하차를 요청한 정류장 변경하기
const updateRequestGetOffBusStopId = (turnId: string, busStopId?: string) => {
  const targetFavoriteLine = favoriteLines.value.find(
    (favoriteLine) => favoriteLine.turnId === turnId && favoriteLine.isBoarding
  );

  if (!targetFavoriteLine) return;

  targetFavoriteLine.requestGetOffBusStopId = busStopId;
};
provide(PROVIDE_KEY_UPDATE_GET_OFF_BUS_STOP_ID, updateRequestGetOffBusStopId);

/**
 * 앱에서 백그라운드에 오래 있다가 다시 진입 시 새로고침 발생
 * 이때 onActivated 호출이 안되는 현상 방지
 */
const excuteActivatedMethods = async () => {
  getFavoriteLines();
  await getLineInfos();
  getSurveyInfo();
};

const handleNoAppInstalledErrorIfNeeded = () => {
  if (route.query.app_not_installed?.toString() !== "true") return;
  showErrorModal(
    "결제오류",
    "결제시 선택하신 카드사 앱이 설치되어 있지 않아 결제할 수 없습니다. 앱 설치 후 다시 이용해주세요."
  );
};

/** 예약 실패 모달 처리 */
const handleBookFailErrorModal = () => {
  const { bookFail, resultCode } = route.query;
  const resultCodeNumber = isNaN(Number(resultCode))
    ? undefined
    : Number(resultCode);

  if (bookFail !== "true") return;

  if (resultCode === "408100001") {
    showErrorModal(
      "예약 실패",
      "예약 시간 만료로 인해 예약이 완료되지 않았습니다.",
      resultCodeNumber
    );
  } else {
    showAlertForBookFail();
  }

  // 노쇼 페널티 등으로 예약이 불가능한 상황일때 유저정보 갱신
  if (resultCode === ERROR_CODE_BOOK_BLOCKED.toString()) {
    userStore.getUserInfo();
  }
};

const handleCheckPasswordExpired = inject(
  PROVIDE_KEY_HANDLE_CHECK_PASSWORD_EXPIRED
) as HandleCheckPasswordExpired;

const getUserRemainBookCount = async () => {
  if (!serviceInfo.value?.shouldShowRemainBookCount) return;

  const result = await userStore.getRemainBookCount();

  if (!result.isSuccess) {
    const { errorMessage, resultCode, errorTime } = result;
    showErrorModal(
      "잔여예약 횟수 조회 오류",
      errorMessage,
      resultCode,
      errorTime
    );
    return;
  }
};

/** LifeCycle */
const isCalledOnMounted = ref(false);

onMounted(async () => {
  isCalledOnMounted.value = true;
  getNoticeInfos();
  await getUserInfo();
  // getServiceEvaluationContents();
  updateFcmToken();
  handleBookFailErrorModal();
  excuteActivatedMethods();
  handleNoAppInstalledErrorIfNeeded();
  handleCheckPasswordExpired();
  getUserRemainBookCount();
});

onBeforeUnmount(() => {
  removeIntervalBusLocation();
  removeIntervalOnBoardLine();
});

onActivated(() => {
  if (isCalledOnMounted.value) return;
  excuteActivatedMethods();
});

onDeactivated(() => {
  removeIntervalBusLocation();
  removeIntervalOnBoardLine();

  isCalledOnMounted.value = false;
});

const __returned__ = { emit, showToastMessage, showErrorModal, router, route, serviceStore, serviceInfo, shouldShowFavoriteLinesCardView, userStore, surveyStore, lineStore, bbsStore, lineName, isBookBlocked, isBoardBlocked, blockReleaseDtmHtmlStr, userInfo, favoriteLines, shouldShowAlertForPushDisabled, shouldShowRequestReview, shouldShowMyPage, cardUpdateKey, allCardsUpdateKey, transitionName, noticeInfos, latestNoticeInfo, shouldShowSurveyNotice, showSurveyNoticeModal, onCloseSurveyNotice, shouldShowBoardingLineInfoModal, toggleShowBoardingLineInfoModal, handleClickBoardingStatusButton, shouldShowServiceEvaluation, toggleShowServiceEvaluation, showNoticeModal, checkCookieExists, shouldShowAlertForBookFail, showAlertForBookFail, shouldShowReportModal, toggleShowReportModal, shouldShowBusImage, toggleShowBusImage, targetBusStop, showBusStopLocation, shouldShowMap, toggleShowMap, shouldShowBusStopLayoutModal, toggleShowBusStopLayoutModal, showReviewModal, showOpinionModal, shouldShowOpinion, onClickPleasant, toggleShowOpinionPage, busLocationUpdateIntervalMillis, lastRefreshTime, updateBusLocation, currentFavoriteLine, isInsideShuttle, updateCurrentFavoriteLine, updateFavoriteLine, onChangedCheck, currentLocation, busLocations, turnBusLastSeatCountList, updateHasAllBusesArrivedLastStop, updateHasAllBusesEndedDriving, isBusRunning, updateBusRunningStatus, shouldGetBusLocation, isLoggedIn, lastBusStopArrivalThresholdMin, getFirstStop, getLastStop, getBusLocation, intervalBusLocation, setIntervalBusLocation, removeIntervalBusLocation, remainingTimeStr, getLastBusStopEventIndex, getLastEventTime, updateRemainingTimeStr, isNotificationAllowed, pushTargetStop, stopClassStr, getTitleOfPushOnAlert, updatePushStatusOnCard, disablePushMessage, shouldShowAlertForPushIsNotAllowed, showAlertForPushIsNotAllowedIfNeeded, hasAnyBusStopNotificationIsOn, pushSettingGuide, goToAppStore, goToLineInfoDetail, targetBusStopForTimeTable, shouldShowTimeTable, toggleShowTimeTable, showTimeTable, getUserInfo, getNoticeInfos, updateFcmToken, getSurveyInfo, getLineInfos, getFavoriteLines, onBoardLine, fullSeatLineRouteCount, getOnBoardLine, onBoardRequestIntervalMillis, intervalOnBoardLine, setIntervalOnBoardLine, removeIntervalOnBoardLine, updateRequestGetOffBusStopId, excuteActivatedMethods, handleNoAppInstalledErrorIfNeeded, handleBookFailErrorModal, handleCheckPasswordExpired, getUserRemainBookCount, isCalledOnMounted, Opinion, Page, MapOfBusStop, Modal, MainNavigationBar, SurveyNoticeModal, ServiceEvaluation, ReportModal, TimeTableModal, BusInfoModal, BusStopLayoutModal, UserBlockingMessage, WelcomeMessages, FavoriteListView, UtilityButton, FavoriteCardsView, BoardingLineInfoModal, CampaignBannerSwiper, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})